import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripePromise, fetchStripeConfig, useStripeConfig } from "../lib/stripe";
import { useAI } from "../hooks/useAI";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { COLORS, SANS, formStyles, pageStyles } from "../components/dispute/theme";
import StripeTestCardHint from "../components/dispute/StripeTestCardHint";

function formatMoney(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function MatchForm({ uuid, amount, onMatched }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmErr } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: `${window.location.origin}/dispute/${uuid}` },
    });
    if (confirmErr) {
      setError(confirmErr.message || "Payment failed.");
      setSubmitting(false);
      return;
    }
    const result = await onMatched();
    if (result?.error) {
      setError(result.error);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <div style={styles.payError}>{error}</div>}
      <button
        type="button"
        onClick={handlePay}
        disabled={!stripe || submitting}
        style={{ ...formStyles.primaryButton, ...(submitting ? formStyles.primaryButtonDisabled : {}), marginTop: 18 }}
      >
        {submitting ? "Processing…" : `Match ${formatMoney(amount)} stake`}
      </button>
      <p style={styles.disabledHint}>Cards are processed securely by Stripe.</p>
    </div>
  );
}

// /match-stake/:uuid — the disputant matches the filer's stake to create the pot.
// Public: the disputant may be a guest arriving via the invitation link.
export default function StakeMatch() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { fetchSharedDispute, matchStake, finalizeStakeMatch } = useAI();
  const { loading: stripeLoading, configured: stripeConfigured } = useStripeConfig();

  const [dispute, setDispute] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  // Guard against React 18 StrictMode firing useEffect twice in dev — without
  // this, matchStake() would create two PaymentIntents on mount.
  const startedRef = useRef(false);

  const load = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    const data = await fetchSharedDispute(uuid);
    if (data.error) {
      if (data.status === 401) navigate("/login");
      else setNotFound(true);
      return;
    }
    setDispute(data);
    // Awaited rather than read from useStripeConfig: this runs once (startedRef)
    // and the config request may still be in flight on first render, which would
    // otherwise skip creating the PaymentIntent entirely.
    const stripeCfg = await fetchStripeConfig();
    if (data.stake_amount && !data.stake_matched_at && stripeCfg.configured) {
      const pi = await matchStake(uuid);
      if (pi.error) setError(pi.error);
      else setClientSecret(pi.clientSecret);
    }
  }, [uuid, fetchSharedDispute, matchStake]);

  useEffect(() => {
    load();
  }, [load]);

  const onMatched = async () => {
    // The stake PI can still be "processing" the moment confirmPayment resolves;
    // it reaches "succeeded" within a few seconds. Poll match-finalize a few
    // times before giving up (the webhook also fulfills it as a backstop) so a
    // still-settling match isn't shown as a failure.
    let result;
    for (let attempt = 0; attempt < 10; attempt++) {
      result = await finalizeStakeMatch(uuid);
      if (!result?.pending) break;
      await new Promise((r) => setTimeout(r, 3000));
    }
    if (result?.error) return { error: result.error };
    if (result?.pending) {
      // Still processing after ~30s — the webhook will record the match server-side.
      return {
        error:
          "Your payment is still processing. It’ll be confirmed automatically in a moment — refresh this page shortly to see the matched stake.",
      };
    }
    navigate(`/dispute/${uuid}`);
    return {};
  };

  if (notFound) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Dispute not found.</div>
      </div>
    );
  }
  if (!dispute) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Loading…</div>
      </div>
    );
  }

  const stake = dispute.stake_amount;
  const matched = !!dispute.stake_matched_at;

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={styles.stack}>
          <div style={styles.headerCard}>
            <h1 style={{ ...pageStyles.pageTitle, color: "#fff", margin: 0 }}>Match the stake</h1>
            <p style={{ ...pageStyles.pageSubtitle, color: "rgba(255,255,255,0.78)", margin: "8px 0 0 0" }}>
              {dispute.subject || "(no subject)"}
            </p>
          </div>

          <div style={styles.sectionCard}>
            {!stake ? (
              <p style={styles.hint}>This dispute has no stake to match.</p>
            ) : matched ? (
              <div style={styles.paidNote}>
                <span>✅ The stake has been matched.</span>
                <Link to={`/dispute/${uuid}`} style={styles.linkBtn}>View dispute →</Link>
              </div>
            ) : (
              <>
                <p style={styles.lead}>
                  The filer staked <strong>{formatMoney(stake)}</strong>. Match it to put it to the panel —
                  the winner takes 75% of the combined pot. If you don't match, the filer's hold is released
                  and the panel still rules on the case.
                </p>
                {stripeLoading ? (
                  <p style={styles.hint}>Loading secure payment form…</p>
                ) : !stripeConfigured ? (
                  <div style={styles.setupNote}>Card payment isn't enabled yet (Stripe keys not set).</div>
                ) : (
                  <>
                    <StripeTestCardHint />
                    {error ? (
                      <div style={styles.payError}>{error}</div>
                    ) : !clientSecret ? (
                      <p style={styles.hint}>Loading secure payment form…</p>
                    ) : (
                      <Elements stripe={getStripePromise()} options={{ clientSecret, appearance: { theme: "stripe" } }}>
                        <MatchForm uuid={uuid} amount={stake} onMatched={onMatched} />
                      </Elements>
                    )}
                  </>
                )}
              </>
            )}
            <Link to={`/dispute/${uuid}`} style={styles.backLink}>← Back to dispute</Link>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  loading: {
    display: "flex",
    height: "60vh",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  stack: { width: "100%", maxWidth: 560, display: "flex", flexDirection: "column", gap: 20 },
  headerCard: {
    width: "100%",
    background: COLORS.dark,
    color: "#fff",
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.10)",
    border: `1px solid ${COLORS.dark}`,
  },
  sectionCard: {
    width: "100%",
    background: COLORS.card,
    borderRadius: "20px 20px 20px 4px",
    padding: "28px 36px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  lead: { color: COLORS.inkSoft, fontSize: 14, lineHeight: 1.6, margin: "0 0 18px 0" },
  hint: { color: COLORS.inkSoft, fontSize: 13, margin: 0 },
  setupNote: {
    background: COLORS.highlight,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 10,
    padding: "14px 16px",
    fontSize: 13,
    color: COLORS.ink,
  },
  payError: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    margin: "12px 0",
  },
  paidNote: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    fontSize: 14,
    color: COLORS.ink,
  },
  linkBtn: { color: COLORS.ink, fontWeight: 600, textDecoration: "none" },
  disabledHint: { color: COLORS.inkMuted, fontSize: 12, margin: "10px 0 0 0", textAlign: "center" },
  backLink: {
    display: "inline-block",
    marginTop: 16,
    fontSize: 12,
    color: COLORS.inkSoft,
    textDecoration: "none",
    borderBottom: `1px dashed ${COLORS.borderStrong}`,
  },
};
